<script lang="ts" setup>
import BillingNotice from '@/components/business/members/BillingNotice.vue'
import SimpleBaseDialog from '@/components/dialogs/SimpleBaseDialog.vue'
import type { BillingNoticeProps } from '#shared/types/billingNotice'

const props = withDefaults(
  defineProps<{
    mode: 'assign' | 'revoke'
    isBulk?: boolean
    memberName?: string
    count?: number
    signerCount?: number
    loading?: boolean
  }>(),
  { isBulk: false, memberName: '', count: 1, signerCount: 0, loading: false }
)

const emit = defineEmits<{
  action: [contextData: Dialogs['business/DialogAdminRole']['contextData']]
  close: []
}>()

const { t } = useI18n()
const { id: businessId } = storeToRefs(useBusinessStore())
const widgetStore = useWidgetStore()
const { logError } = useErrorHandling()
const { fetchSeatStats } = useSeatStatsFetch(businessId)
const { getBreakdown } = useSeatUpgradeBreakdown(businessId)
const { isUnlimited } = useUserPlanInfo()

const needsBilling = props.mode === 'assign' && props.signerCount > 0 && !isUnlimited.value

const billing = ref<BillingNoticeProps>()
const isResolvingSeats = computed(() => needsBilling && !billing.value)

onMounted(async () => {
  if (!needsBilling) return

  try {
    billing.value = getBreakdown(await fetchSeatStats(), props.signerCount)
  } catch (error) {
    logError('Failed to read seat stats for the admin-role dialog', error)
    widgetStore.createSnackbar({ message: t('global.general_error'), color: 'error' })
    emit('close')
  }
})

const titleKeypath = computed(() =>
  props.mode === 'revoke' ? 'business.members.admin_role.revoke.title' : 'business.members.admin_role.assign.title'
)

const billingBreakdown = computed(() => (needsBilling ? billing.value : undefined))

const { trackAbandoned } = useBillingNoticeTracking(() =>
  billingBreakdown.value
    ? {
        context: 'admin_promotion',
        fromSeat: 'signer',
        toSeat: 'full',
        memberCount: props.signerCount,
        breakdown: billingBreakdown.value,
      }
    : undefined
)

const close = () => {
  trackAbandoned()
  emit('close')
}

const assignTitle = computed(() =>
  props.isBulk
    ? t('business.members.admin_role.assign.title_bulk', { count: props.count }, props.count)
    : t('business.members.admin_role.assign.title', { name: props.memberName })
)

const revokeTitle = computed(() =>
  props.isBulk
    ? t('business.members.admin_role.revoke.title_bulk', { count: props.count }, props.count)
    : t('business.members.admin_role.revoke.title', { name: props.memberName })
)

const upgradeDescription = computed(() =>
  props.isBulk
    ? t('business.members.admin_role.assign.description_upgrade_bulk', { count: props.signerCount }, props.signerCount)
    : t('business.members.admin_role.assign.description_upgrade')
)

const upgradeActionText = computed(() =>
  billing.value && billing.value.newlyPurchased > 0
    ? t('business.members.admin_role.assign.confirm_upgrade_pay', props.signerCount)
    : t('business.members.admin_role.assign.confirm_upgrade', props.signerCount)
)

const dialogConfig = computed(() => {
  if (props.mode === 'revoke') {
    return {
      title: revokeTitle.value,
      description: t('business.members.admin_role.revoke.description', props.count),
      actionText: t('business.members.actions.revoke_admin'),
    }
  }
  const config = {
    title: assignTitle.value,
    description: t('business.members.admin_role.assign.description'),
    actionText: t('business.members.actions.make_admin'),
  }
  if (props.signerCount > 0) {
    config.description = upgradeDescription.value
    config.actionText = upgradeActionText.value
  }
  return config
})
</script>

<template>
  <simple-base-dialog
    :title="dialogConfig.title"
    :action-text="dialogConfig.actionText"
    :cancel-text="t('global.not_now')"
    cancel-color="grey-dark"
    max-width="580"
    :loading="loading"
    :is-content-loading="isResolvingSeats"
    data-cy="admin_role_dialog"
    @action="emit('action', { newlyPurchased: billingBreakdown?.newlyPurchased ?? 0 })"
    @close="close"
  >
    <template v-if="!isBulk" #title>
      <i18n-t :keypath="titleKeypath" tag="span">
        <template #name>
          <span class="admin-role-name">{{ memberName }}</span>
        </template>
      </i18n-t>
    </template>
    <p>
      {{ dialogConfig.description }}
    </p>
    <billing-notice v-if="billingBreakdown" class="admin-role-billing" v-bind="billingBreakdown" />
  </simple-base-dialog>
</template>

<style lang="scss" scoped>
.admin-role-name {
  color: $c-grey-dark;
}

.admin-role-billing {
  margin-top: 9 * $bw;
}
</style>
